import { useForm } from "react-hook-form";
import { Box, Typography, Button } from "@mui/material";
import theme from "@/theme";
import { yupResolver } from "@hookform/resolvers/yup";
import { schemaCreateUsername } from "../scheme";
import { FC, useEffect, useState } from "react";
import { RegisterUserRequest } from "@/store/api/authApiData";
import { styles } from "../stylesSignUp";
import { useCheckUniqueMutation } from "@/store/api/authApi";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import HeaderControl from "./HeaderControl";

type FormInput = {
  username: string;
};

interface CreateUsernameProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setSignUpData: (data: Partial<RegisterUserRequest>) => void;
  signUpData: RegisterUserRequest;
}

const CreateUsername: FC<CreateUsernameProps> = ({ setSignUpStep, setSignUpData, signUpData }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schemaCreateUsername),
    mode: "onChange",
  });

  const [checkUnique, { isLoading: isCheckingUnique }] = useCheckUniqueMutation();
  const [uniqUsername, setUniqUsername] = useState<boolean | undefined>(true);
  const [isSettled, setIsSettled] = useState(true);

  const usernameValue = watch("username");

  const onSubmit = async (data: FormInput) => {
    if (isCheckingUnique) {
      return;
    }
    try {
      const res = await checkUnique({ username: data.username }).unwrap();
      if (!res.username) {
        setUniqUsername(false);
        return;
      }
    } catch {
      // transient network error — don't block sign-up on it, same as the live-check behavior
    }
    setSignUpData({ ...signUpData, username: data.username });
    setSignUpStep(4);
  };

  useEffect(() => {
    setIsSettled(false);

    const timeout = setTimeout(async () => {
      if (!usernameValue) {
        setUniqUsername(true);
        setIsSettled(true);
        return;
      }

      try {
        const res = await checkUnique({ username: usernameValue }).unwrap();
        setUniqUsername(res.username);
      } catch {
        setUniqUsername(true);
      } finally {
        setIsSettled(true);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [usernameValue]);

  return (
    <Box sx={styles.wrapper}>
      <HeaderControl onClick={() => setSignUpStep(2)} />

      <Typography
        component="h1"
        variant="h2"
        color={theme.palette.custom.blueGray900}
        fontWeight={600}
        lineHeight={"2.25rem"}
        textAlign={"center"}
        fontSize={"28px"}
      >
        Create Your Username
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          width: "100%",
          marginTop: "3.25rem",
        }}
      >
        <CustomInput
          label="Username"
          placeholder="Enter your username"
          error={!!errors.username || (isSettled && uniqUsername === false)}
          helperText={
            errors.username?.message ||
            (isSettled && !uniqUsername ? "Username already exists" : null)
          }
          register={register}
          name="username"
          value={usernameValue ?? signUpData.username}
          onChange={(e) => {
            register("username").onChange(e);
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={
            !isSettled ||
            (!signUpData.username && !usernameValue) ||
            !!errors.username ||
            !uniqUsername ||
            isCheckingUnique
          }
          sx={styles.continueButton}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default CreateUsername;
